import Close from "@mui/icons-material/Close";
import LockIcon from '@mui/icons-material/Lock';
import { Avatar, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, Radio, RadioGroup, Typography, useTheme } from '@mui/material';
import { MaterialReactTable } from 'material-react-table';
import * as React from 'react';
import { useLoadLeagueData } from '../../../../app/dataLoaderHooks';
import { useAppSelector } from '../../../../app/hooks';
import { League, LeaguePhase, Player, PlayerPoachInfo, RosterPositionEnum, Team, TradeProposalPlayer } from '../../../../sdk/model';
import PlayerCardLink from '../../../cards/playerCardLink';
import { isPlayerLocked } from '../../../util/PlayerDataUtil';
import playerMatchupDisplay from '../../../util/PlayerMatchupDisplay';
import { positionOrderNoBench } from '../../../util/PositionOrder';
import { ACTIVE_LEAGUE_PHASES, getAction, getActionButtons, getBenchData, getBid, getFilteredBenchData, getIrData, getPlayerInfo, getPlayersForTrade, getPsData, getStarterData, getSwapOption, isRequireConfirmation, playerLocked, SWAP_BUTTON_MAP, swapSpotsAfterConfirm } from '../../../util/RosterUtils';
import { toSalary } from '../../../util/SalaryDisplay';
import { positionSorter } from '../../../util/sortingUtils';
import { getMaxTeamSize, MIN_WEEKS_ON_IR } from "../../../util/TeamUtils";
import TradeDetails from '../../trade/TradeDetails';
import { ExpandableRow } from './ExpandableRow';
import LoadingModal from '../../../util/LoadingModal';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

interface teamRosterProps {
  leagueId: string,
  team: Team,
  playerMap: Map<string | undefined, Player | undefined>,
  currentSeasonYear: number,
  rosterSettings: Map<string | undefined, number | undefined>,
  myTeam: boolean,
  commish: boolean
}

function TeamRoster(props: teamRosterProps){
  console.log(new Error().stack);
  
  const [selectedPosition, setSelectedPosition] = React.useState<string | undefined>(undefined);
  const [otherSelectedPosition, setOtherSelectedPosition] = React.useState<string | undefined>(undefined);
  const [roster, setRoster] = React.useState(props.team.playerRoster);
  const [errors, setErrors] = React.useState<string[]>([]);
  const [processing, setProcessing] = React.useState<boolean>(false);
  const [columnVisibility, setColumnVisibility] = React.useState({});
  
  const [isException, setIsException] = React.useState(false);
  const [isLoadComplete, setLoadComplete] = React.useState(false);
  
  const theme = useTheme();
  
  const user = useAppSelector((state) => state.user);
  const league : League|undefined = useLoadLeagueData({
    leagueId: props.leagueId, 
    userId: user.id, 
    loadBasicDraftData: false,
    loadFullDraftData: false, 
    loadContractData: true,
    loadBasicAuctionData: false, 
    loadFullAuctionData: false,
    loadRosterData: false, 
    loadWaivedContracts: false,
    loadRosterFor: [],  // Add this line
    loadScheduleData: false,  // Add this line
    isException: isException, 
    setIsException: setIsException,
    isLoadComplete: isLoadComplete, 
    setLoadComplete: setLoadComplete
  });
  const playerPoachInfo : Array<PlayerPoachInfo> | undefined = useAppSelector((state) => state.playerPoachDataLoad)[props.team.id!];
  
  React.useEffect(() => {
    setRoster(props.team.playerRoster);
  }, [props.team.playerRoster])

  React.useEffect(() => {
    setColumnVisibility({
      previousYearSalary : league?.phase === LeaguePhase.Offseason,
      nextYearSalary: false,
      currentMatchup: ACTIVE_LEAGUE_PHASES.has(league?.phase),
      select: props.myTeam
    })
  }, [props.myTeam, league])
  
  const nflGames = useAppSelector((state) => state.nflGameDataLoad);
  const nflGameMap = new Map(nflGames.map(nflGame => [nflGame.id!, nflGame]));
  
  const selectedPlayerInfo = getPlayerInfo(selectedPosition, props.team, roster);
  const selectedPlayer = selectedPlayerInfo ? props.playerMap.get(selectedPlayerInfo.playerId)! : null;
  
  const otherSelectedPlayerInfo = getPlayerInfo(otherSelectedPosition, props.team, roster);
  const otherSelectedPlayer = otherSelectedPlayerInfo ? props.playerMap.get(otherSelectedPlayerInfo.playerId)! : null;

  function setErrorsAndFocus(errors) {
    setErrors(errors);
    document.getElementById("errors")?.focus();
  }

  function swapSpots(otherPosition) {
    const position1Info = selectedPosition?.split('-')!;
    const position2Info = otherPosition?.split('-')!;
    if (position1Info[0] !== position2Info[0] &&
        (isRequireConfirmation(position1Info) || isRequireConfirmation(position2Info))) {
      setOtherSelectedPosition(otherPosition);
    } else {
      swapSpotsAfterConfirm(
        position1Info,
        position2Info,
        selectedPlayerInfo,
        otherSelectedPlayerInfo,
        props.team,
        bid,
        setRoster,
        setSelectedPosition,
        setOtherSelectedPosition,
        setErrorsAndFocus,
        setProcessing,
        props.commish)
    }
    
  }
  
  function handleActionResponse(type, message) {
    if (type === 'error') {
      setErrorsAndFocus([message]);
    } else {
      setErrors([]);
      setSelectedPosition(undefined);
    }
  }

  // Setup Starters
  
  let startersColumns = 
    [
      {
        accessorKey: 'id',
        id: 'select',
        header: 'Select',
        minSize: 15,
        size: 15,
        enableSorting: false,
        Cell: ({ renderedCellValue, row }) => {
          if (!props.commish && playerLocked(row.original.player, row.original.teamPlayer, row.original.position, league)) {
            return <LockIcon />
          }
          if (selectedPosition && selectedPosition !== row.id) {
            const position1Info = selectedPosition.split('-');
            const position2Info = row.id.split('-');
            const swapOption = getSwapOption(position1Info, position2Info, roster, props.playerMap);
            const swapButton = SWAP_BUTTON_MAP.get(swapOption);
            if (swapButton) {
              return <IconButton onClick={() => swapSpots(row.id)}>
                {swapButton}
              </IconButton>
            }
            return null;
          }

          if ((row.original.position === RosterPositionEnum.Ir || row.original.position === RosterPositionEnum.Ps) && !row.original.teamPlayer) {
            // Just prevent folks from being able to select empty IR spots to make for one less thing we have to take into consideration
            // Also prevent folks from selecting empty PS spots
            return null;
          }
         
          return <FormControlLabel value={renderedCellValue} control={<Radio />} label="" onClick={() => {
            if (selectedPosition) {
              setSelectedPosition(undefined);
            }
          }} />
        }
      },
      {
        accessorKey: 'contractTypes',
        id: 'contractTypes',
        header: 'Contract Tags',
        minSize: 15,
        size: 15,
        enableSorting: false,
        Cell: ({ renderedCellValue, row }) => 
          renderedCellValue.map(contractType => 
            <Avatar title={contractType.details} sx={{display: 'inline-flex', marginRight: 1}}>{contractType.display}</Avatar>
          )
      },
      {
        accessorKey: 'position',
        id: 'position',
        header: 'Position',
        minSize: 15,
        size: 15,
        enableSorting: true,
        Cell: ({ renderedCellValue }) => renderedCellValue,
        sortingFn: 'position'
      },
      {
        accessorKey: 'player',
        id: 'player',
        header: 'Player',
        minSize: 15,
        size: 15,
        Cell: ({ renderedCellValue, row }) => {
          const player = renderedCellValue;
          const swapPlayer = row.original.autoSwapPlayer ? props.playerMap.get(row.original.autoSwapPlayer) : undefined;
          return player ? <Box>
              <PlayerCardLink player={player} leagueId={props.leagueId} />
              {!!swapPlayer && <IconButton title={swapPlayer.abbr_name}>
                <SwapHorizIcon sx={{color: "orange"}} />
              </IconButton>}
            </Box> : 'Empty';
        },
        sortingFn: positionSorter(positionOrderNoBench)
      },
      {
        accessorKey: 'player',
        id: 'currentMatchup',
        header: 'Current Matchup',
        minSize: 15,
        size: 15,
        Cell: ({ renderedCellValue }) => {
          const player : Player | undefined = renderedCellValue;
          const currentWeekStats = player?.quick_stats?.currentWeeksStats;
          const nflGame = currentWeekStats ? nflGameMap.get(currentWeekStats.gameId!) : undefined;
          return player && player.nfl_team ? playerMatchupDisplay(currentWeekStats, nflGame) : 'N/A'
        }
      },
      {
        accessorKey: 'projection',
        id: 'projection',
        header: 'Projected Score',
        minSize: 15,
        size: 15,
        Cell: ({ renderedCellValue }) => 
          renderedCellValue ? renderedCellValue.toFixed(2) : "0.00"
        
      },
      {
        accessorKey: 'player.quick_stats.byeWeek',
        header: 'Bye',
        minSize: 15,
        size: 15,
        Cell: ({ renderedCellValue }) => 
          renderedCellValue ? renderedCellValue : '-'
      },
      {
        accessorKey: 'previousYearSalary',
        id: 'previousYearSalary',
        header: (props.currentSeasonYear - 1) + ' Salary',
        minSize: 15,
        size: 15,
        Cell: ({ renderedCellValue, row }) => renderedCellValue ? toSalary(renderedCellValue) : '-'
      },
      {
        accessorKey: 'currentYearSalary',
        id: 'currentYearSalary',
        header: props.currentSeasonYear + ' Salary',
        minSize: 15,
        size: 15,
        Cell: ({ renderedCellValue, row }) => renderedCellValue ? toSalary(renderedCellValue) : '-'
      },
      {
        accessorKey: 'nextYearSalary',
        id: 'nextYearSalary',
        header: (props.currentSeasonYear + 1) + ' Salary',
        minSize: 15,
        size: 15,
        Cell: ({ renderedCellValue, row }) => renderedCellValue ? toSalary(renderedCellValue) : '-'
      },
      {
        accessorKey: 'finalYearOfContract',
        id: 'finalYearOfContract',
        header: 'Final Year of Contract',
        minSize: 15,
        size: 15,
        Cell: ({renderedCellValue, row }) => renderedCellValue || '-'
      }
    ];


  const starterData = getStarterData(roster!, playerPoachInfo, props.currentSeasonYear, props.playerMap, league);

  // Setup Bench

  const benchColumns = [...startersColumns].filter(column => column.id !== 'position');

  benchColumns.splice(1, 0, {
    accessorKey: 'player.position',
    id: 'position',
    header: 'Position',
    minSize: 15,
    size: 15,
    enableSorting: true,
    Cell: ({ renderedCellValue }) => renderedCellValue ?? 'Empty',
    sortingFn: 'position'
  })
  
  
  let benchData = getBenchData(roster!, playerPoachInfo, starterData, props.rosterSettings!, props.currentSeasonYear, props.playerMap, league);
  benchData = getFilteredBenchData(benchData, selectedPosition, selectedPlayer, roster!, props.playerMap, playerPoachInfo, props.currentSeasonYear, league!);
  
  // Setup IR

  const irColumns = [...benchColumns];

  irColumns.push({
    accessorKey: 'teamPlayer.weeksSpentOnIr',
    id: 'weeksSpentOnIr',
    header: 'Weeks Spent on IR',
    minSize: 15,
    size: 15,
    enableSorting: false,
    Cell: ({ renderedCellValue }) => renderedCellValue ?? '-'
  })

  const irData = getIrData(roster!, playerPoachInfo, props.currentSeasonYear, props.playerMap, league);

  // Setup PS

  const psColumns = [...benchColumns].filter(column => column.id !== 'contractTypes' && column.id !== 'finalYearOfContract');
  psColumns.splice(1, 0, {
    accessorKey: 'poachOffer',
    id: 'poachOffer',
    header: 'Has Poach Offer?',
    minSize: 15,
    size: 15,
    Cell: ({renderedCellValue }) => renderedCellValue ? 'Yes' : 'No'
  })
  
  const psData = getPsData(props.team, playerPoachInfo, props.currentSeasonYear, props.playerMap, league);
  
  const actionButtons = getActionButtons({  
    selectedPlayer: selectedPlayer!,
    selectedPlayerInfo: selectedPlayerInfo!,
    league: league!,
    team: props.team,
    currentSeasonYear: props.currentSeasonYear,
    handleActionResponse: handleActionResponse,
    setRoster: setRoster,
    selectedPosition: selectedPosition,
    setSelectedPosition: setSelectedPosition,
    commish: props.commish,
    playerMap: props.playerMap
  });
  
  const teamMap = new Map([[props.team.id, props.team]]);
  const teamPlayerMap = new Map(Object.values(props.team.playerRoster!).flatMap(playerList => 
    playerList.filter(player => player).map(player => [player.playerId, player])
  ));
  const maxRosterSize = getMaxTeamSize(league!);
  
  let bid = getBid(playerPoachInfo, selectedPlayer?.id);
  const players : TradeProposalPlayer[] = getPlayersForTrade(selectedPlayer, otherSelectedPlayer, 
              selectedPosition, otherSelectedPosition, props.team, bid, playerPoachInfo);

  props.team.practiceSquad?.filter(player => player).forEach(player => teamPlayerMap.set(player.playerId, player));
  let errorInfo = errors.map((error, index) => {
    return (<p className='error' key={'error_' + index}>{error}</p>);
  });
  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <LoadingModal show={processing} />
      {/* Error Messages */}
      {errors.length > 0 && (
        <Box 
          component="div"
          tabIndex={0}
          id="errors"
          sx={{ 
            mb: 2, 
            p: 2, 
            bgcolor: 'error.light', 
            color: 'error.contrastText',
            borderRadius: 1,
            fontSize: '1rem'
          }}
        >
          {errors.map((error, index) => (
            <Typography key={`error-${index}`} sx={{ fontSize: 'inherit' }}>{error}</Typography>
          ))}
        </Box>
      )}
  
      {/* Action Buttons */}
      {actionButtons && (
        <Box sx={{ 
          mb: 2, 
          display: 'flex', 
          gap: 1, 
          flexWrap: 'wrap',
          '& > *': {
            flex: '1 1 auto',
            minWidth: '120px',
            '& .MuiButton-root': {
              width: '100%',
              padding: '6px 16px',
              fontSize: '0.875rem'
            }
          },
          flexDirection: 'row'
        }}>
          {React.Children.map(actionButtons, (button, index) => (
            <Box key={index} sx={{ width: '100%' }}>
              {React.cloneElement(button as React.ReactElement, {
              })}
            </Box>
          ))}
        </Box>
      )}
      <Dialog open={otherSelectedPosition !== undefined} maxWidth="lg" fullWidth>
        <DialogTitle>{selectedPosition?.startsWith(RosterPositionEnum.Ir) || otherSelectedPosition?.startsWith(RosterPositionEnum.Ir) ? "IR" : "Practice Squad"} Transaction</DialogTitle>
        <Box position="absolute" top={0} right={0}>
          <IconButton onClick={() => {
            setSelectedPosition(undefined);
            setOtherSelectedPosition(undefined);
          }}>
            <Close />
          </IconButton>
        </Box>
        <DialogContent>
          <Typography>
            {selectedPosition?.startsWith(RosterPositionEnum.Ir) || otherSelectedPosition?.startsWith(RosterPositionEnum.Ir) ? <React.Fragment>
              Are you sure you want to make this IR transaction? 
              Players on IR must spend at least {MIN_WEEKS_ON_IR} weeks during the NFL Regular Season on IR before being reactivated. 
            </React.Fragment> : <React.Fragment>
              Are you sure you want to activate this player from your Practice Squad to your Active Roster for <b>1yr/{toSalary(bid)}</b>? 
            </React.Fragment>}
            &nbsp;This will have the following impact on your team's salary cap:
            <TradeDetails leagueId={props.leagueId} teams={[props.team]} currentSeason={props.currentSeasonYear} players={players} picks={[]} playerMap={props.playerMap} teamMap={teamMap} teamPlayerMap={teamPlayerMap} pickMap={new Map()} salaryCap={league!.currentSeason!.nflSeason!.salaryCap!} maxRosterSize={maxRosterSize} />
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button color="primary" variant="contained" onClick={() => {
            setSelectedPosition(undefined);
            setOtherSelectedPosition(undefined);
          }}>
            Cancel
          </Button>
          <Button color="secondary" variant="contained" onClick={() => swapSpotsAfterConfirm(selectedPosition?.split('-')!, otherSelectedPosition?.split('-')!, selectedPlayerInfo, otherSelectedPlayerInfo, props.team, bid, setRoster, setSelectedPosition, setOtherSelectedPosition, setErrorsAndFocus, setProcessing, props.commish)}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
  
      <RadioGroup 
        key={selectedPosition || null} // Force re-render when selection changes
        value={selectedPosition} 
        name="selectedPosition" 
        onChange={(e) => {
          // Only update if the value is different to prevent unnecessary re-renders
          if (e.target.value !== selectedPosition) {
            console.log('RadioGroup onChange:', e.target.value);
            setSelectedPosition(e.target.value);
          }
        }}
        sx={{ display: 'block' }}
      >
        {/* Starters Table */}
        <Box sx={{ mb: 3, overflowX: 'auto' }}>
          <Typography variant="h6" component="div" color="text.secondary" sx={{ mb: 1, fontSize: '1rem' }}>
            Starters:
          </Typography>
          <MaterialReactTable
            enableRowSelection={false}
            enableMultiRowSelection={false}
            getRowId={(row) => row.id!}
            columns={startersColumns}
            data={starterData}
            enableRowOrdering={false}
            enableSorting={true}
            enableColumnActions={true}
            enableColumnFilters={false}
            enablePagination={false}
            enableStickyHeader={false}
            enableBottomToolbar={false}
            enableTopToolbar={false}
            muiTableContainerProps={{
              sx: { 
                '&::-webkit-scrollbar': {
                  width: '8px',
                  height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: theme.palette.mode === 'light' ? '#f1f1f1' : '#424242',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: theme.palette.mode === 'light' ? '#888' : '#666',
                  borderRadius: '4px',
                  '&:hover': {
                    background: theme.palette.mode === 'light' ? '#777' : '#888',
                  },
                },
              }
            }}
            // Table head cell props are now handled in the columns definition
            muiTableBodyCellProps={{
              sx: {
                py: 1,
                px: 1.5,
                fontSize: '0.875rem'
              }
            }}
            displayColumnDefOptions={{
              'mrt-row-actions': {
                header: 'Actions',
                size: 100,
              },
            }}
            state={{
              density: 'compact',
              columnVisibility: {
                // Merge with any additional column visibility settings
                ...columnVisibility
              }
            }}
            muiTableHeadCellProps={{
              sx: {
                "& .Mui-TableHeadCell-Content-Labels": {
                  padding: "0px",
                },
                "& .MuiBox-root": {
                  padding: "0px",
                },
              },
            }}
            muiTableBodyRowProps={({ row }) => {
              console.log('Rendering row:', row.id);
              return {
              component: 'tr',
              children: (
                <ExpandableRow
                  row={row.original}
                  columns={startersColumns}
                  isMobile={false}
                  selectedPosition={selectedPosition}
                  onRowClick={setSelectedPosition}
                  getSwapOption={getSwapOption}
                  roster={roster}
                  playerMap={props.playerMap}
                />
              )
            }}}
          />
        </Box>
  
        {/* Bench Table */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" component="div" color="text.secondary" sx={{ mb: 1 }}>
            Bench:
          </Typography>
          <MaterialReactTable
            columns={benchColumns}
            data={benchData}
            getRowId={(row) => row.id!}
            enableColumnActions={true}
            enableSorting={true}
            enableFilters={false}
            enablePagination={false}
            enableGrouping
            // groupedColumnMode="remove"
            enableStickyHeader={false}
            enableStickyFooter={false}
            sortingFns={{
              position: positionSorter(positionOrderNoBench)
            }}  
            state={{
              grouping: ['position'],
              expanded: true,
              sorting: [
                { id: 'position', desc: false }
              ],
              columnVisibility: {
                // Merge with any additional column visibility settings
                ...columnVisibility
              }
            }}
            muiTableContainerProps={{
              sx: { 
                '&::-webkit-scrollbar': {
                  width: '8px',
                  height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: theme.palette.mode === 'light' ? '#f1f1f1' : '#424242',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: theme.palette.mode === 'light' ? '#888' : '#666',
                  borderRadius: '4px',
                  '&:hover': {
                    background: theme.palette.mode === 'light' ? '#777' : '#888',
                  },
                },
              }
            }}
            muiTableHeadCellProps={{
              sx: {
                "& .Mui-TableHeadCell-Content-Labels": {
                  padding: "0px",
                },
                "& .MuiBox-root": {
                  padding: "0px",
                },
              },
            }}
            muiTableBodyRowProps={({ row }) => ({
              component: 'tr',
              children: (
                <ExpandableRow
                  row={row.original}
                  columns={benchColumns}
                  isMobile={false}
                  selectedPosition={selectedPosition}
                  onRowClick={setSelectedPosition}
                  getSwapOption={getSwapOption}
                  roster={roster}
                  playerMap={props.playerMap}
                />
              )
            })}
            displayColumnDefOptions={{
              'mrt-row-expand': {
                enableResizing: false,
                size: 60,
              },
            }}
            enableColumnResizing={false}
            enableColumnDragging={false}
            enableFullScreenToggle={false}
            enableDensityToggle={false}
            enableHiding={false}
            enableTopToolbar={false}
          />
        </Box>
  

     

        {/* IR Table */}
        {irData.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" component="div" color="text.secondary" sx={{ mb: 1 }}>
              IR:
            </Typography>
            <MaterialReactTable
              enableRowSelection={false}
              enableMultiRowSelection={false}
              getRowId={(row) => row.id!}
              columns={irColumns}
              data={irData}
              enableRowOrdering={false}
              enableSorting={true}
              enableFilters={false}
              enablePagination={false}
              enableStickyHeader={false}
              state={{
                density: 'compact',
                columnVisibility: {
                  // Merge with any additional column visibility settings
                  ...columnVisibility
                }
              }}
              muiTableContainerProps={{
                sx: { 
                  '&::-webkit-scrollbar': {
                    width: '8px',
                    height: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: theme.palette.mode === 'light' ? '#f1f1f1' : '#424242',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: theme.palette.mode === 'light' ? '#888' : '#666',
                    borderRadius: '4px',
                    '&:hover': {
                      background: theme.palette.mode === 'light' ? '#777' : '#888',
                    },
                  },
                }
              }}
              muiTableBodyRowProps={({ row }) => {
                console.log('Rendering row:', row.id);
                return {
                  component: 'tr',
                  children: (
                    <ExpandableRow
                      key={row.id}
                      row={row.original}
                      columns={irColumns}
                      isMobile={false}
                      selectedPosition={selectedPosition}
                      onRowClick={setSelectedPosition}
                      getSwapOption={getSwapOption}
                      roster={roster}
                      playerMap={props.playerMap}
                    />
                  )
                };
              }}
            />
          </Box>
        )}

        {/* Practice Squad Table */}
        {psData.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" component="div" color="text.secondary" sx={{ mb: 1 }}>
              Practice Squad:
            </Typography>
            <MaterialReactTable
              enableRowSelection={false}
              enableMultiRowSelection={false}
              getRowId={(row) => row.id!}
              columns={psColumns}
              data={psData}
              enableRowOrdering={false}
              enableSorting={true}
              enableFilters={false}
              enablePagination={false}
              enableStickyHeader={false}
              state={{
                density: 'compact',
                columnVisibility: {
                  // Merge with any additional column visibility settings
                  ...columnVisibility
                }
              }}
              muiTableContainerProps={{
                sx: { 
                  '&::-webkit-scrollbar': {
                    width: '8px',
                    height: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: theme.palette.mode === 'light' ? '#f1f1f1' : '#424242',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: theme.palette.mode === 'light' ? '#888' : '#666',
                    borderRadius: '4px',
                    '&:hover': {
                      background: theme.palette.mode === 'light' ? '#777' : '#888',
                    },
                  },
                }
              }}
              muiTableBodyRowProps={({ row }) => {
                console.log('Rendering row:', row.id);
                return {
                  component: 'tr',
                  children: (
                    <ExpandableRow
                      key={row.id}
                      row={row.original}
                      columns={psColumns}
                      isMobile={false}
                      selectedPosition={selectedPosition}
                      onRowClick={setSelectedPosition}
                      getSwapOption={getSwapOption}
                      roster={roster}
                      playerMap={props.playerMap}
                    />
                  )
                };
              }}
            />
          </Box>
        )}
      </RadioGroup>
    </Box>
  );
}

export default TeamRoster;
